import ReactDOM from "react-dom";
import { RootRoute } from "@app/const";
import { getGlobalSettings } from "@app/stores";
import { defaultLanguage, DesignChannelType } from "@app/types";
import { renderByRoute } from "@app/utilities";
import BaseComponent from "./BaseComponent";
import { LanguageLabel } from "./LanguageLabel";
import LocaleTextarea from "./LocaleTextarea";
import { PromptAudioController } from "./prompt/prompt-audio-controller";

/**
 * JSDoc imports.
 *
 * @import { PromptUsedInModule } from '@app/utilities/functions';
 */

/**
 * @typedef LocaleModuleNodeGroupProps
 * @type {object}
 * @prop {PromptUsedInModule[]} prompts
 */

/** @extends BaseComponent<LocaleModuleNodeGroupProps, never>; */
export default class LocaleModuleNodeGroup extends BaseComponent {
  validateInput({ currentTarget }) {
    return currentTarget.value !== '';
  }

  getDomSelf() {
    return $(ReactDOM.findDOMNode(this));
  }

  setConfig(key, newValue) {
    if (key !== "prompt") {
      return;
    }
    const { lang, value, idx, channelLayer } = newValue;
    const { isOmniChannel } = window.apm.serialized.omniChannel;
    const updatedMod = this.props.mod;
    const flowNode = updatedMod.versions[1].flow.nodes[idx];
    if (!flowNode) {
      return;
    }

    const { channel, text } = flowNode.props.config;
    if (isOmniChannel) {
      channel[channelLayer] ??= {};
      channel[channelLayer][lang] = value;
    } else {
      text[lang] = value;
    }

    flowNode.props.lastLocalChange = window.apm.timestamp;
    //update the components object and all the modules it is used in
    window.apm.updateParentNode(flowNode, flowNode.props.parentComponentID, "channel", channel, "object");
    window.apm.setSerializedChanged();
    window.apm.postEventToCurrentView({ target: "request-refresh" });
    renderByRoute(RootRoute.settings);
  }

  render() {
    const {
      channel: channelLayer,
      language: languageCode
    } = getGlobalSettings();
    const { serialized } = window.apm;
    const {
      languages,
      omniChannel: { isOmniChannel }
    } = serialized;

    const { mod, prompts } = this.props;
    const primaryLanguage = languages[0];
    const selectedLanguage = languages.find(({ language }) => language === languageCode);
    const hideSelectedLanguage = !selectedLanguage || selectedLanguage === primaryLanguage;
    const isVoiceChannel = !isOmniChannel || channelLayer === DesignChannelType.voice;

    return (
      <div className="panel-group" role="tablist">
        <div className="panel panel-default">
          <div className="panel-heading" role="tab" style={{ padding: "0", paddingLeft: "15px" }}>
            <div className="row">
              <div className="col-md-10" style={{ padding: "10px" }}>
                <i className={`fe icon-${mod.props.iconClass ?? "package"}`} />
                &nbsp;&nbsp;{mod.name}
              </div>
            </div>
          </div>
          <div className="panel-collapse collapse in" role="tabpanel" data-tracker={mod.id}>
            <div className="panel-body">
              <div className="row">
                <table className="table table-condensed">
                  <thead>
                    <tr style={{ color: "var(--theme-text-muted)", fontSize: "14px" }}>
                      <td style={{ width: "20%" }}>{window.l.settings.prompt_node_name}</td>
                      <td style={{ width: "40%" }}>
                        <LanguageLabel language={primaryLanguage} includeTts includeVt />
                      </td>
                      <td style={{ width: "40%" }}>
                        {(hideSelectedLanguage) ? null : (
                          <LanguageLabel language={selectedLanguage} includeTts includeVt />
                        )}
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {prompts.map(({
                      index,
                      primaryLanguageText,
                      prompt,
                      parentComponentID,
                      selectedLanguageText
                    }) => {
                      return (
                        <tr key={prompt.id}>
                          <td>
                            <label>{(prompt.name) ? prompt.name : "Name is Empty"}</label>
                            {isOmniChannel && <p style={{ color: 'var(--theme-text-muted)' }}>Channel: {channelLayer}</p>}
                          </td>
                          <td>
                            <LocaleTextarea
                              rowsVal={4}
                              defaultValue={primaryLanguageText}
                              valKey="prompt"
                              channelLayer={channelLayer}
                              idx={index}
                              context={this}
                              type="prompt"
                              parentComponentID={parentComponentID}
                              language={languageCode}
                              isEnUS={true}
                            />
                            {(!isVoiceChannel) ? null : (
                              <PromptAudioController
                                language={primaryLanguage}
                                node={prompt}
                                nodeModule={mod}
                                skipInitialFetch
                                ttsVoice={primaryLanguage.ttsFallBackVoice}
                              />
                            )}
                          </td>
                          <td>
                            {(hideSelectedLanguage) ? null : (
                              <>
                                <LocaleTextarea
                                  rowsVal={4}
                                  defaultValue={selectedLanguageText}
                                  valKey="prompt"
                                  channelLayer={channelLayer}
                                  idx={index}
                                  context={this}
                                  type="prompt"
                                  parentComponentID={parentComponentID}
                                  language={languageCode}
                                  isEnUS={selectedLanguage.language === defaultLanguage}
                                />
                                {(!isVoiceChannel) ? null : (
                                  <PromptAudioController
                                    language={selectedLanguage}
                                    node={prompt}
                                    nodeModule={mod}
                                    skipInitialFetch
                                    ttsVoice={selectedLanguage.ttsFallBackVoice}
                                  />
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
